import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import LoginPage from './auth/LoginPage'
import RequireAuth from './shell/RequireAuth'
import AppShell from './shell/AppShell'
import Dashboard from './shell/Dashboard'
import NotFound from './shell/NotFound'
import Protected from './shell/Protected'

// Primary module routes
const LeadEngine   = lazy(() => import('./modules/lead-engine/LeadEngine'))
const DealDesk     = lazy(() => import('./modules/deal-desk/DealDesk'))
const Projects     = lazy(() => import('./modules/projects/Projects'))
const ServiceDesk  = lazy(() => import('./modules/tickets/ServiceDesk'))
const ReportingModule = lazy(() => import('./modules/reporting/ReportingModule'))
const Admin        = lazy(() => import('./modules/admin/AdminModule'))
const ProfileModule = lazy(() => import('./modules/profile/ProfileModule'))

const Gallery      = lazy(() => import('./design/Gallery'))

function ModuleFallback() {
  return (
    <div style={{ padding: 'var(--space-8)', color: 'var(--tx2)' }}>
      Loading…
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Navigate to="/app" replace />} />

          {/* Protected app shell */}
          <Route element={<RequireAuth />}>
            <Route path="/app" element={<AppShell />}>
              <Route index element={<Dashboard />} />

              {/* ── Primary module routes ── */}
              <Route
                path="leads/*"
                element={
                  <Protected feature="lead_engine.view">
                    <Suspense fallback={<ModuleFallback />}>
                      <LeadEngine />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="deals/*"
                element={
                  <Protected feature="deal_desk.view">
                    <Suspense fallback={<ModuleFallback />}>
                      <DealDesk />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="projects/*"
                element={
                  <Protected feature="project_mgmt.view">
                    <Suspense fallback={<ModuleFallback />}>
                      <Projects />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="tickets/*"
                element={
                  <Protected feature="ticket.view">
                    <Suspense fallback={<ModuleFallback />}>
                      <ServiceDesk />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="reports/*"
                element={
                  <Protected feature="reporting.view">
                    <Suspense fallback={<ModuleFallback />}>
                      <ReportingModule />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="admin/*"
                element={
                  <Protected feature="admin.users">
                    <Suspense fallback={<ModuleFallback />}>
                      <Admin />
                    </Suspense>
                  </Protected>
                }
              />

              <Route
                path="profile"
                element={
                  <Suspense fallback={<ModuleFallback />}>
                    <ProfileModule />
                  </Suspense>
                }
              />

              {/* ── Backward-compat redirects (merged into tab structure) ── */}
              {/* Lead Engine tabs */}
              <Route path="crm"      element={<Navigate to="/app/leads" replace />} />
              <Route path="workflow" element={<Navigate to="/app/leads" replace />} />
              {/* Sales Engine tabs */}
              <Route path="opportunities" element={<Navigate to="/app/deals" replace />} />
              <Route path="proposals"     element={<Navigate to="/app/deals" replace />} />
              <Route path="forecast"      element={<Navigate to="/app/deals" replace />} />
              {/* Intelligence tabs */}
              <Route path="timeline"      element={<Navigate to="/app/reports" replace />} />
              <Route path="client-portal" element={<Navigate to="/app/reports" replace />} />
              {/* Settings tabs */}
              <Route path="notification-settings" element={<Navigate to="/app/admin" replace />} />
              <Route path="ghl"        element={<Navigate to="/app/admin" replace />} />
              <Route path="website"    element={<Navigate to="/app/admin" replace />} />
              <Route path="onboarding" element={<Navigate to="/app/leads" replace />} />

              {/* Catch-all → dashboard */}
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>
          </Route>

          {/* Design system gallery — dev/internal */}
          <Route
            path="/design"
            element={
              <Suspense fallback={<ModuleFallback />}>
                <Gallery />
              </Suspense>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
